// types/common.ts

// ── Entity type registry ────────────────────────────────────────────────────
// Single source of truth for entity type identifiers, backend IDs, and labels.

export const ENTITY_TYPES = {
    SYSTEM: { id: 0, key: 'system', label: 'System' },
    BROADCASTER: { id: 3, key: 'broadcaster', label: 'Broadcaster' },
    ADTECH: { id: 4, key: 'adtech', label: 'AdTech Provider' },
    AGENCY: { id: 2, key: 'agency', label: 'Agency' },
    CUSTOMER: { id: 1, key: 'customer', label: 'Customer' },
    DISTRIBUTOR: { id: 7, key: 'distributor', label: 'Distributor' },
} as const;

export type EntityType = keyof typeof ENTITY_TYPES;

// ── Lookup helpers ──────────────────────────────────────────────────────────

const _byId = new Map<number, EntityType>(Object.entries(ENTITY_TYPES).map(([k, v]) => [v.id, k as EntityType]));
const _byKey = new Map<string, EntityType>(Object.entries(ENTITY_TYPES).map(([k, v]) => [v.key, k as EntityType]));

/** Backend integer ID → EntityType (e.g. 2 → 'AGENCY') */
export const entityTypeFromId = (id: number): EntityType | undefined => _byId.get(id);

/** Lowercase key → EntityType (e.g. 'agency' → 'AGENCY'). Case-insensitive. */
export const entityTypeFromKey = (key: string): EntityType | undefined => _byKey.get(key.toLowerCase());

/** EntityType → backend integer ID (e.g. 'AGENCY' → 2) */
export const entityTypeId = (et: EntityType): number => ENTITY_TYPES[et].id;

/** EntityType → lowercase key (e.g. 'AGENCY' → 'agency') */
export const entityTypeKey = (et: EntityType): string => ENTITY_TYPES[et].key;

/** EntityType → display label (e.g. 'ADTECH' → 'AdTech Provider') */
export const entityTypeLabel = (et: EntityType): string => ENTITY_TYPES[et].label;

// ── Permission sets ─────────────────────────────────────────────────────────
// Which entity types can manage which other entity types.

export const CAN_MANAGE_AGENCIES: ReadonlySet<EntityType> = new Set([
    'BROADCASTER',
    'CUSTOMER',
    'ADTECH',
    'DISTRIBUTOR',
] as const);

export const CAN_MANAGE_CLIENTS: ReadonlySet<EntityType> = new Set([
    'AGENCY',
    'BROADCASTER',
    'ADTECH',
    'DISTRIBUTOR',
] as const);

// ── Other shared types ──────────────────────────────────────────────────────

export type UserRole = 'ROLE_ADMIN' | 'ROLE_USER';

export type PermissionLevel = 'SUPER_ADMIN' | 'ADMIN' | 'USER';

export interface CountryInfo {
    id: number;
    name: string;
    iso_code: string;
}

/** Generic wrapper returned by handleRequest() */
export interface ApiResult<T = unknown> {
    status: 'success' | 'failure';
    data?: T;
    error?: string;
    message?: string;
}

/** Reusable select option shape (used with react-select) */
export interface SelectOption<T = number> {
    value: T;
    label: string;
}
